<template>
    <div 
        class="asset-item" 
        :class="{ 
            'active': opened,
            'background': background,
            'border': border,
            'overflow': overflow,
            'selectable': isSelectable,
            'selected': selected
        }"
        @click="handleSelect" 
        @contextmenu="handleContext"
        @mouseenter="hovered = true"
        @mouseleave="hovered = false">

        <!-- image -->
        <ViewerImage 
            class="image" 
            :asset="asset" 
            :fileKey="fileKey" 
            :animation="animation" 
            :animated="isAnimated" 
            @ready="handleReady" 
            @error="handleError" />

        <!-- interface -->
        <div class="interface">

            <v-speed-dial
                v-if="hasContext"
                location="top center"
                transition="slide-y-transition" 
                v-model="opened">

                <template v-slot:activator="{ props }">
                    <v-btn 
                        v-bind="props"
                        elevation="1"
                        color="darkbase"
                        size="small" 
                        class="speed-dial-button">
                        <v-tooltip
                            activator="parent"
                            location="right">
                            more options
                        </v-tooltip>
                        <v-icon icon="fa-solid fa-ellipsis-vertical"></v-icon>
                    </v-btn>
                </template>

                <v-btn 
                    key="1" 
                    elevation="1"
                    color="darkbase"
                    class="speed-dial-button"
                    @click="handleShare">
                    <v-tooltip
                        activator="parent"
                        location="right">
                        share asset
                    </v-tooltip>
                    <v-icon icon="fa-solid fa-share-nodes"></v-icon>
                </v-btn>
                <v-btn 
                    key="2" 
                    elevation="1"
                    color="darkbase"
                    class="speed-dial-button"
                    @click="handleDownload">
                    <v-tooltip
                        activator="parent"
                        location="right">
                        download asset
                    </v-tooltip>
                    <v-icon icon="fa-solid fa-file-arrow-down"></v-icon>
                </v-btn>
                <v-btn 
                    key="3" 
                    elevation="1"
                    color="error"
                    class="speed-dial-button"
                    @click="handleDelete">
                    <v-tooltip
                        activator="parent"
                        location="right">
                        delete asset
                    </v-tooltip>
                    <v-icon icon="fa-solid fa-trash"></v-icon>
                </v-btn>
            </v-speed-dial>

        </div>

    </div>
</template>

<script>
import DIALOGS from '@generio-ai/library-legacy/constants/dialogs'
import { mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import { ViewerImage } from '@generio-ai/library-legacy/components/viewer'

export default {
    name: 'AssetItem',
    components: {
        ViewerImage
    },
    props: {
        asset: {
            type: Object,
            default: null
        },
        fileKey: {
            type: String,
            default: null // if null, use thumbnail
        },
        overflow: {
            type: Boolean,
            default: false
        },
        context: {
            type: Boolean,
            default: true
        },
        selectable: {
            type: Boolean,
            default: true
        },
        persist: {
            type: Boolean,
            default: false
        },
        background: {
            type: Boolean,
            default: true
        },
        animation: {
            type: String,
            default: 'skeleton',
            validate(value) {
                return ['skeleton', 'progress', 'dots', 'none'].includes(value)
            }
        },
        animated: {
            type: Boolean,
            default: true
        },
        border: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            hovered: false,
            opened: false,
            ready: false,
            error: false,
            selected: false
        }
    },
    computed: {
        isAnimated() {
            return this.animated || this.hovered
        },
        isSelectable() {
            return this.selectable && this.asset !== null && (this.ready || this.error)
        },

        hasContext() {
            return this.context && this.asset !== null && this.ready && (this.hovered || this.opened)
        },
        
        resource() {
            if (this.fileKey && this.asset?.files && this.asset?.files[this.fileKey]) {
                return this.asset.files[this.fileKey]
            }
            return this.asset?.thumbnail
        },
        data() {
            return this.resource?.data || null
        },
    },
    watch: {
        selected(newValue, oldValue) {
            if (newValue === oldValue) return
            if (!newValue) this.selected = false
        }
    },
    emits: [
        'select',
        'share',
        'download',
        'delete'
    ],
    methods: {
        ...mapActions('dialogs', [
            'open'
        ]),
        ...mapActions('library', [
            'deleteAsset'
        ]),

        select() {
            this.selected = true
        },
        unselect() {
            this.selected = false
        },

        handleReady(asset) {
            this.ready = true
        },
        handleError(error) {
            this.ready = false
            this.error = true
        },
        handleSelect() {
            if (!this.isSelectable) return
            if (this.error) {
                this.open({
                    component: DIALOGS.CHOICE,
                    payload: {
                        title: 'Broken&nbsp;Asset',
                        message: 'Do you want to delete the broken asset?',
                        firstLabel: 'Cancel',
                        firstColor: 'surface',
                        onFirst: () => {},
                        secondLabel: 'Delete',
                        secondColor: 'error',
                        onSecond: () => {
                            this.handleDelete()
                        }
                    }
                })
            }
            if (this.data === null) return
            if (this.asset === null) return
            if (this.persist) {
                this.selected = !this.selected
            }
            this.$emit('select', this.asset, this.selected)
        },
        handleContext(event) {
            event.preventDefault()
            if (!this.context) return
            if (this.asset === null) return
            this.opened = true
            document.dispatchEvent(new Event('click'))
        },
        handleShare() {
            this.open({
                component: 'share',
                payload: {
                    asset: this.asset
                }
            })
            this.$emit('share', this.asset)
        },
        handleDownload() {
            this.open({
                component: 'download',
                payload: {
                    asset: this.asset
                }
            })
            this.$emit('download', this.asset)
        },
        handleDelete() {
            this.deleteAsset({
                asset: this.asset
            })
            this.$emit('delete', this.asset)
        }
    }
}
</script>

<style>
.asset-item .image img {
    filter: brightness(1.0);
    transform: scale(1);
    transition: transform 0.3s ease, filter 0.3s ease;
}

.asset-item.selectable:hover .image img,
.asset-item.active .image img,
.asset-item.selected .image img {
    filter: brightness(1.1);
    transform: scale(1.2);
}

.asset-item.overflow .image {
    overflow: visible;
}

.speed-dial-button {
    min-height: 36px;
}
</style>

<style scoped>
.asset-item {
    display: block;
    position: relative;
    width: 100%;
    height: auto;
    aspect-ratio: 1 / 1;
    border-radius: 8px;
    transition: border 0.3s ease;
}

.asset-item::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: none;
    filter: blur(0px);
    transition: filter 0.3s ease;
    z-index: 0;
    pointer-events: none;
}

.asset-item.active.border {
    border: 2px solid rgb(var(--v-theme-color1)) !important;
}

.asset-item.active.background::after {
    background: radial-gradient(
        ellipse closest-corner,
        rgba(var(--v-theme-color1), 0.5) 0%,
        transparent 70%
    );
    filter: blur(5px);
}

.asset-item.background::after {
    background: radial-gradient(
        ellipse closest-corner,
        rgba(var(--v-theme-background), 0.5) 0%,
        transparent 70%
    );
}

.asset-item.border {
    border: 2px solid transparent !important;
}

.asset-item.selectable {
    cursor: pointer;
}

.asset-item.selectable.border:hover {
    border: 2px solid rgb(var(--v-theme-color1)) !important;
}

.asset-item.selectable.background:hover::after {
    background: radial-gradient(
        ellipse closest-corner,
        rgba(var(--v-theme-color1), 0.5) 0%,
        transparent 70%
    );
    filter: blur(5px);
}

.asset-item.selected {
    background-color: rgba(var(--v-theme-color1), 0.3);
}

.asset-item.selected.border {
    border: 2px solid rgba(var(--v-theme-color1), 0.3) !important;
}

.asset-item .image {
    position: relative;
    z-index: 1;
}

.asset-item .interface {
    z-index: 1;
}

.asset-item .frame {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.asset-item .interface {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
    padding: 10px;
    box-sizing: border-box;
    display: flex;
    align-items: flex-end;
    justify-content: flex-start;
}
</style>